import { useQuery } from "@rocicorp/zero/react";
import { queries } from "../../../../shared/queries";
import { displaySpaceName } from "../../../../shared/spaceName";
import { spaceIcon } from "../../../lib/spaceIcon";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Icon } from "../../Icon";
import {
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SearchableDropdown,
} from "../../SearchableDropdown";
import { Text } from "../../Text";
import { DropdownOpener } from "../shared";

export function RouteSpacePicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange?: (spaceId: string | null) => void;
}) {
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const [personalSpaces] = useQuery(queries.personalSpaceOwners());
  const owners = new Map(
    personalSpaces.map((space) => [space.id, space.roleGrantsAsScope[0]?.user])
  );
  const options = [
    {
      id: null,
      label: "All spaces",
      icon: "layers" as const,
      searchText: "All spaces",
    },
    ...spaces
      .map((space) => {
        const owner = owners.get(space.id);
        const label = displaySpaceName(
          space,
          null,
          owner ? userDisplayName(owner) : undefined
        );
        return {
          id: space.id,
          label,
          icon: spaceIcon(space.mode),
          searchText: `${label} ${owner?.email ?? ""}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const label =
    options.find((option) => option.id === value)?.label ?? "Selected space";
  if (!onChange) return <Text variant="text-small">{label}</Text>;
  return (
    <SearchableDropdown
      style={{ width: "100%", minWidth: 0 }}
      opener={
        <DropdownOpener as="span" style={{ minWidth: 0, gap: 8 }}>
          <Text variant="text-regular" ellipsis>
            {label}
          </Text>
          <Icon name="chevron-down" size={12} style={{ flexShrink: 0 }} />
        </DropdownOpener>
      }
      align="left"
      searchable={options.length >= MIN_OPTIONS_FOR_SEARCH}
      searchPlaceholder="Search spaces…"
    >
      {(query, close) => {
        const matches = options.filter((option) =>
          option.searchText.toLowerCase().includes(query.toLowerCase())
        );
        return matches.length > 0 ? (
          matches.map((option) => (
            <RowButton
              key={option.id ?? "all"}
              onClick={() => {
                onChange(option.id);
                close();
              }}
            >
              <Icon name={option.icon} size={16} />
              <Text variant="text-regular" ellipsis>
                {option.label}
              </Text>
            </RowButton>
          ))
        ) : (
          <Text variant="text-small">No matching spaces</Text>
        );
      }}
    </SearchableDropdown>
  );
}
